import {
  useMutation,
  useQuery,
  useQueryClient,
  type UseMutationResult,
  type UseQueryResult,
} from '@tanstack/react-query';
import type {
  CreateProductInput,
  PaginatedResult,
  Product,
  SortDirection,
  UpdateProductInput,
} from '@catalyst-psa/shared';

import { api } from '../../api/client.js';
import { buildQuery } from '../../api/queryString.js';

export interface ProductListParams {
  page: number;
  pageSize: number;
  search?: string;
  sku?: string;
  name?: string;
  customerDescription?: string;
  serviceCategoryId?: string;
  qboClass?: string;
  billingTiming?: string;
  psaClass?: string;
  isActive?: boolean;
  sortBy?: string;
  sortDir?: SortDirection;
}

const productKeys = {
  all: ['products'] as const,
  list: (params: ProductListParams) => ['products', 'list', params] as const,
  detail: (id: string) => ['products', 'detail', id] as const,
};

/** Every active product as a full DTO for selection dropdowns (unpaginated). */
export function useProductOptions(): UseQueryResult<Product[]> {
  return useQuery({
    queryKey: [...productKeys.all, 'options'] as const,
    queryFn: () => api.get<Product[]>('/products/options'),
  });
}

export function useProducts(params: ProductListParams): UseQueryResult<PaginatedResult<Product>> {
  return useQuery({
    queryKey: productKeys.list(params),
    queryFn: () =>
      api.get<PaginatedResult<Product>>(
        `/products${buildQuery({
          page: params.page,
          pageSize: params.pageSize,
          search: params.search,
          sku: params.sku,
          name: params.name,
          customerDescription: params.customerDescription,
          serviceCategoryId: params.serviceCategoryId,
          qboClass: params.qboClass,
          billingTiming: params.billingTiming,
          psaClass: params.psaClass,
          isActive: params.isActive,
          sortBy: params.sortBy,
          sortDir: params.sortDir,
        })}`,
      ),
  });
}

export function useCreateProduct(): UseMutationResult<Product, unknown, CreateProductInput> {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: CreateProductInput) => api.post<Product>('/products', input),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: productKeys.all });
    },
  });
}

export interface UpdateProductArgs {
  id: string;
  input: UpdateProductInput;
}

export function useUpdateProduct(): UseMutationResult<Product, unknown, UpdateProductArgs> {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, input }: UpdateProductArgs) => api.patch<Product>(`/products/${id}`, input),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: productKeys.all });
    },
  });
}

export function useDeleteProduct(): UseMutationResult<void, unknown, string> {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => api.delete(`/products/${id}`),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: productKeys.all });
    },
  });
}
